import { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";

const empty = {
  name: "", poster: "", description: "", date: "", time: "", venue: "", address: "",
  map_link: "", status: "upcoming", featured: false, ticket_categories: [], artist_ids: [],
};

export const EventFormModal = ({ event, artists, onClose, onSaved }) => {
  const [form, setForm] = useState(event ? { ...empty, ...event } : { ...empty });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addCat = () => set("ticket_categories", [...form.ticket_categories, { id: crypto.randomUUID(), name: "", price: 0, quantity: 0, sold: 0 }]);
  const updCat = (i, k, v) => {
    const cats = [...form.ticket_categories];
    cats[i] = { ...cats[i], [k]: k === "name" ? v : Number(v) };
    set("ticket_categories", cats);
  };
  const delCat = (i) => set("ticket_categories", form.ticket_categories.filter((_, idx) => idx !== i));

  const toggleArtist = (id) => {
    const ids = form.artist_ids.includes(id) ? form.artist_ids.filter((x) => x !== id) : [...form.artist_ids, id];
    set("artist_ids", ids);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Event name required"); return; }
    setSaving(true);
    const payload = {
      name: form.name, poster: form.poster, description: form.description, date: form.date,
      time: form.time, venue: form.venue, address: form.address, map_link: form.map_link,
      status: form.status, featured: form.featured,
      ticket_categories: form.ticket_categories, artist_ids: form.artist_ids,
      gallery: form.gallery || [],
    };
    try {
      if (event) await api.put(`/admin/events/${event.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success("Event saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-black/80 p-4 py-10" data-testid="event-form-modal">
      <div className="card-dark w-full max-w-2xl p-6 sm:p-8">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-serif text-2xl font-bold">{event ? "Edit Event" : "Create Event"}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="event-form-close"><X size={22} /></button>
        </div>

        <div className="space-y-4">
          <Field label="Event Name *"><input className="input-field" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="event-name-input" /></Field>
          <ImageUpload label="Poster" value={form.poster} onChange={(v) => set("poster", v)} testid="event-poster-upload" />
          <Field label="Description"><textarea rows={3} className="input-field" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="event-desc-input" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date"><input className="input-field" placeholder="e.g. 24 Dec 2026" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="event-date-input" /></Field>
            <Field label="Time"><input className="input-field" placeholder="e.g. 7:00 PM" value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="event-time-input" /></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Venue"><input className="input-field" value={form.venue} onChange={(e) => set("venue", e.target.value)} data-testid="event-venue-input" /></Field>
            <Field label="Address"><input className="input-field" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="event-address-input" /></Field>
          </div>
          <Field label="Map Link"><input className="input-field" value={form.map_link} onChange={(e) => set("map_link", e.target.value)} data-testid="event-map-input" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Status">
              <select className="input-field" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="event-status-select">
                <option value="upcoming">Upcoming</option>
                <option value="coming_soon">Coming Soon</option>
                <option value="past">Past</option>
              </select>
            </Field>
            <label className="flex cursor-pointer items-center gap-3 pt-7">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[#D4AF37]" data-testid="event-featured-checkbox" />
              <span className="text-sm text-zinc-300">Featured on homepage</span>
            </label>
          </div>

          {/* Ticket categories */}
          <div className="rounded-md border border-[#2A2A32] p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="caption">Ticket Categories</p>
              <button onClick={addCat} className="flex items-center gap-1 text-sm text-[#E5C158]" data-testid="add-category-button"><Plus size={15} /> Add</button>
            </div>
            <div className="space-y-2">
              {form.ticket_categories.map((c, i) => (
                <div key={c.id} className="grid grid-cols-12 gap-2" data-testid={`category-row-${i}`}>
                  <input className="input-field col-span-5" placeholder="Name (e.g. VIP)" value={c.name} onChange={(e) => updCat(i, "name", e.target.value)} data-testid={`category-name-${i}`} />
                  <input className="input-field col-span-3" placeholder="Price" type="number" value={c.price} onChange={(e) => updCat(i, "price", e.target.value)} data-testid={`category-price-${i}`} />
                  <input className="input-field col-span-3" placeholder="Qty" type="number" value={c.quantity} onChange={(e) => updCat(i, "quantity", e.target.value)} data-testid={`category-qty-${i}`} />
                  <button onClick={() => delCat(i)} className="col-span-1 flex items-center justify-center text-red-400" data-testid={`category-del-${i}`}><Trash2 size={16} /></button>
                </div>
              ))}
              {!form.ticket_categories.length && <p className="text-xs text-zinc-500">No categories. Add at least one to sell tickets.</p>}
            </div>
          </div>

          {/* Artists */}
          <div className="rounded-md border border-[#2A2A32] p-4">
            <p className="caption mb-3">Assign Artists</p>
            {artists.length ? (
              <div className="flex flex-wrap gap-2">
                {artists.map((a) => {
                  const on = form.artist_ids.includes(a.id);
                  return (
                    <button key={a.id} onClick={() => toggleArtist(a.id)}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#E5C158]" : "border-zinc-700 text-zinc-400"}`}
                      data-testid={`assign-artist-${a.id}`}>{a.name}</button>
                  );
                })}
              </div>
            ) : <p className="text-xs text-zinc-500">No artists yet. Add artists in the Artists tab.</p>}
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={save} disabled={saving} className="btn-gold flex-1" data-testid="event-form-save">{saving ? "Saving..." : "SAVE EVENT"}</button>
          <button onClick={onClose} className="btn-outline">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div><label className="caption mb-2 block">{label}</label>{children}</div>
);
